import z from "zod";

import {
  MAX_EXPLICIT_SKILL_IDS,
  MAX_GITHUB_PATH_LENGTH,
  MAX_GITHUB_REF_LENGTH,
  MAX_GITHUB_REPOSITORY_URL_LENGTH,
  MAX_REFUSED_SKILL_NAMES,
  MAX_RELEASE_SKILL_S3_KEYS_PER_REQUEST,
  MAX_RESOURCE_S3_KEY_LENGTH,
  MAX_SKILL_ALLOWED_TOOLS_LENGTH,
  MAX_SKILL_ARGUMENT_HINT_LENGTH,
  MAX_SKILL_BODY_LENGTH,
  MAX_SKILL_BUNDLE_ARCHIVE_BYTES,
  MAX_SKILL_COMPATIBILITY_LENGTH,
  MAX_SKILL_DESCRIPTION_LENGTH,
  MAX_SKILL_LICENSE_LENGTH,
  MAX_SKILL_NAME_LENGTH,
  MAX_SKILL_PICKER_NAMES,
  MAX_SKILL_RAW_FRONTMATTER_LENGTH,
  MAX_SKILL_RESOURCE_BYTES,
  MAX_SKILL_RESOURCE_FILE_NAME_LENGTH,
  MAX_SKILL_RESOURCE_PATH_BYTES,
  MAX_SKILL_SHARE_RECIPIENTS_PER_REQUEST,
  MAX_SKILL_VERSION_RESOURCE_COUNT,
  MAX_SKILLS_PAGE_SIZE,
  MAX_SKILLS_SEARCH_LENGTH,
  MIN_SKILL_RESOURCE_BYTES,
  MIN_SKILLS_PAGE,
  SKILL_BASE64_SHA256_PATTERN,
  SKILL_COMMIT_SHA_HEX_PATTERN,
  SKILL_NAME_PATTERN,
  SKILL_SHA256_HEX_PATTERN,
} from "../constants/skills";

// NOTE: NSAI-3305 — the Agent Skills wire contract shared by web, mobile and
// the browser extension. Every shape here mirrors a server type; the
// authoritative copies live under apps/nsai/server/src/skills/. Timestamps are
// modelled as strings on purpose: the server declares them `Date`, but they
// reach a client as ISO 8601 JSON strings.

/* -------------------------------------------------------------------------
 * Enums — mirror the server value-for-value. Clients branch on these strings.
 * ---------------------------------------------------------------------- */

export enum ESkillArchivedFilter {
  ACTIVE = "active",
  ARCHIVED = "archived",
  ALL = "all",
}

export enum ESkillVisibilityFilter {
  ALL = "all",
  PRIVATE = "private",
  PUBLIC = "public",
}

// NOTE: NSAI-3306 — the enabled/disabled list filter. Tri-state rather than a
// boolean because "disabled only" and "no preference" have to be distinguishable
// in a query string, which a bare boolean cannot express.
export enum ESkillEnabledFilter {
  ALL = "all",
  ENABLED = "enabled",
  DISABLED = "disabled",
}

export enum ESkillSourceType {
  AUTHORED = "AUTHORED",
  PASTE = "PASTE",
  ZIP = "ZIP",
  GITHUB = "GITHUB",
  ORG_CATALOG = "ORG_CATALOG",
}

// NOTE: SKILL.md manifest failures. Mirrors ESkillManifestErrorCode; each value
// pairs with a `skills.manifest.<lower_snake_code>` message on the server.
export enum ESkillManifestErrorCode {
  INPUT_NOT_STRING = "INPUT_NOT_STRING",
  INPUT_TOO_LARGE = "INPUT_TOO_LARGE",
  INPUT_EMPTY = "INPUT_EMPTY",
  NUL_CHARACTER = "NUL_CHARACTER",
  CONTROL_CHARACTER = "CONTROL_CHARACTER",
  MISSING_OPENING_DELIMITER = "MISSING_OPENING_DELIMITER",
  MISSING_CLOSING_DELIMITER = "MISSING_CLOSING_DELIMITER",
  // NOTE: NSAI-5907 — unclosed frontmatter whose message names the line that
  // nearly closed it, instead of blaming a `---` the author can see is present.
  CLOSING_DELIMITER_MALFORMED = "CLOSING_DELIMITER_MALFORMED",
  EMPTY_FRONTMATTER = "EMPTY_FRONTMATTER",
  FRONTMATTER_TOO_LARGE = "FRONTMATTER_TOO_LARGE",
  MULTIPLE_DOCUMENTS = "MULTIPLE_DOCUMENTS",
  YAML_SYNTAX_ERROR = "YAML_SYNTAX_ERROR",
  DUPLICATE_KEY = "DUPLICATE_KEY",
  ANCHOR_NOT_ALLOWED = "ANCHOR_NOT_ALLOWED",
  ALIAS_NOT_ALLOWED = "ALIAS_NOT_ALLOWED",
  MERGE_KEY_NOT_ALLOWED = "MERGE_KEY_NOT_ALLOWED",
  CUSTOM_TAG_NOT_ALLOWED = "CUSTOM_TAG_NOT_ALLOWED",
  ROOT_NOT_MAPPING = "ROOT_NOT_MAPPING",
  KEY_NOT_STRING = "KEY_NOT_STRING",
  UNKNOWN_FIELD = "UNKNOWN_FIELD",
  VERSION_FIELD_NOT_ALLOWED = "VERSION_FIELD_NOT_ALLOWED",
  FIELD_NOT_STRING = "FIELD_NOT_STRING",
  NAME_REQUIRED = "NAME_REQUIRED",
  NAME_TOO_LONG = "NAME_TOO_LONG",
  NAME_INVALID_FORMAT = "NAME_INVALID_FORMAT",
  // NOTE: NSAI-5792 — raised by the import preview rather than the parser; see the
  // server enum. Mirrored here because a client branches on the code.
  NAME_ALREADY_TAKEN = "NAME_ALREADY_TAKEN",
  DESCRIPTION_REQUIRED = "DESCRIPTION_REQUIRED",
  DESCRIPTION_BLANK = "DESCRIPTION_BLANK",
  DESCRIPTION_TOO_LONG = "DESCRIPTION_TOO_LONG",
  LICENSE_BLANK = "LICENSE_BLANK",
  LICENSE_TOO_LONG = "LICENSE_TOO_LONG",
  COMPATIBILITY_BLANK = "COMPATIBILITY_BLANK",
  COMPATIBILITY_TOO_LONG = "COMPATIBILITY_TOO_LONG",
  ALLOWED_TOOLS_NOT_STRING = "ALLOWED_TOOLS_NOT_STRING",
  ALLOWED_TOOLS_TOO_LONG = "ALLOWED_TOOLS_TOO_LONG",
  METADATA_NOT_MAPPING = "METADATA_NOT_MAPPING",
  METADATA_VALUE_NOT_STRING = "METADATA_VALUE_NOT_STRING",
  METADATA_TOO_MANY_ENTRIES = "METADATA_TOO_MANY_ENTRIES",
  METADATA_KEY_BLANK = "METADATA_KEY_BLANK",
  METADATA_KEY_TOO_LONG = "METADATA_KEY_TOO_LONG",
  METADATA_VALUE_TOO_LONG = "METADATA_VALUE_TOO_LONG",
  BODY_EMPTY = "BODY_EMPTY",
  BODY_TOO_LONG = "BODY_TOO_LONG",
  BODY_TOKEN_LIMIT_EXCEEDED = "BODY_TOKEN_LIMIT_EXCEEDED",
  BODY_RESERVED_TAG = "BODY_RESERVED_TAG",
}

// NOTE: Archive-level failures of a ZIP or GitHub bundle. Mirrors
// ESkillBundleErrorCode; each pairs with `skills.bundle.<lower_snake_code>`.
export enum ESkillBundleErrorCode {
  ARCHIVE_UNREADABLE = "ARCHIVE_UNREADABLE",
  ARCHIVE_EMPTY = "ARCHIVE_EMPTY",
  ARCHIVE_TOO_LARGE = "ARCHIVE_TOO_LARGE",
  ARCHIVE_ENCRYPTED = "ARCHIVE_ENCRYPTED",
  ARCHIVE_SPLIT = "ARCHIVE_SPLIT",
  ARCHIVE_CHANGED = "ARCHIVE_CHANGED",
  UNSUPPORTED_COMPRESSION = "UNSUPPORTED_COMPRESSION",
  TOO_MANY_ENTRIES = "TOO_MANY_ENTRIES",
  ENTRY_TOO_LARGE = "ENTRY_TOO_LARGE",
  EXPANDED_TOO_LARGE = "EXPANDED_TOO_LARGE",
  COMPRESSION_RATIO_EXCEEDED = "COMPRESSION_RATIO_EXCEEDED",
  PATH_DEPTH_EXCEEDED = "PATH_DEPTH_EXCEEDED",
  ENTRY_NAME_INVALID = "ENTRY_NAME_INVALID",
  ENTRY_NOT_REGULAR_FILE = "ENTRY_NOT_REGULAR_FILE",
  ENTRY_SIZE_MISMATCH = "ENTRY_SIZE_MISMATCH",
  ENTRY_HEADER_MISMATCH = "ENTRY_HEADER_MISMATCH",
  DUPLICATE_ENTRY_PATH = "DUPLICATE_ENTRY_PATH",
  ROOT_FILE_NOT_ALLOWED = "ROOT_FILE_NOT_ALLOWED",
  SKILL_ROOT_MISSING = "SKILL_ROOT_MISSING",
  MULTIPLE_SKILL_ROOTS = "MULTIPLE_SKILL_ROOTS",
  MANIFEST_MISSING = "MANIFEST_MISSING",
  MANIFEST_DUPLICATE = "MANIFEST_DUPLICATE",
  MANIFEST_NESTED = "MANIFEST_NESTED",
  MANIFEST_TOO_LARGE = "MANIFEST_TOO_LARGE",
  ROOT_NAME_MISMATCH = "ROOT_NAME_MISMATCH",
  RESOURCE_PATH_INVALID = "RESOURCE_PATH_INVALID",
  RESOURCE_PATH_RESERVED = "RESOURCE_PATH_RESERVED",
  TOO_MANY_RESOURCES = "TOO_MANY_RESOURCES",
  RESOURCES_TOO_LARGE = "RESOURCES_TOO_LARGE",
  SUBMODULE_NOT_SUPPORTED = "SUBMODULE_NOT_SUPPORTED",
  LFS_POINTER_NOT_SUPPORTED = "LFS_POINTER_NOT_SUPPORTED",
  BUNDLE_TIMEOUT = "BUNDLE_TIMEOUT",
}

// NOTE: Warnings never block an import — they are what a preview shows so the
// person importing can decide.
export enum ESkillBundleWarningCode {
  SCRIPTS_PRESENT = "SCRIPTS_PRESENT",
  PLATFORM_METADATA_IGNORED = "PLATFORM_METADATA_IGNORED",
}

// NOTE: GitHub source failures. Mirrors ESkillSourceErrorCode; each pairs with
// `skills.source.<lower_snake_code>`.
export enum ESkillSourceErrorCode {
  REPOSITORY_URL_INVALID = "REPOSITORY_URL_INVALID",
  REPOSITORY_HOST_NOT_ALLOWED = "REPOSITORY_HOST_NOT_ALLOWED",
  REPOSITORY_URL_HAS_CREDENTIALS = "REPOSITORY_URL_HAS_CREDENTIALS",
  REPOSITORY_URL_HAS_EXTRA_PARTS = "REPOSITORY_URL_HAS_EXTRA_PARTS",
  REF_INVALID = "REF_INVALID",
  PATH_INVALID = "PATH_INVALID",
  REPOSITORY_NOT_FOUND = "REPOSITORY_NOT_FOUND",
  REF_NOT_FOUND = "REF_NOT_FOUND",
  PATH_NOT_FOUND = "PATH_NOT_FOUND",
  CONNECTION_NOT_USABLE = "CONNECTION_NOT_USABLE",
  // NOTE: NSAI-5906 — a GitHub connection made through a connected-apps
  // provider. It holds no token of its own, so it can never read a repository,
  // and reconnecting it the same way produces another one exactly like it.
  CONNECTION_NOT_SUPPORTED = "CONNECTION_NOT_SUPPORTED",
  REDIRECT_NOT_ALLOWED = "REDIRECT_NOT_ALLOWED",
  ARCHIVE_TOO_LARGE = "ARCHIVE_TOO_LARGE",
  // NOTE: NSAI-5914 — the SKILL FOLDER is over a limit, as opposed to the
  // repository around it. An import that names one folder must not report the
  // size of everything it did not ask for.
  FOLDER_TOO_LARGE = "FOLDER_TOO_LARGE",
  SOURCE_TIMEOUT = "SOURCE_TIMEOUT",
  RATE_LIMITED = "RATE_LIMITED",
  UPSTREAM_FAILED = "UPSTREAM_FAILED",
  SOURCE_CHANGED = "SOURCE_CHANGED",
}

/* -------------------------------------------------------------------------
 * Shared building blocks
 * ---------------------------------------------------------------------- */

// NOTE: Frontmatter metadata is a flat string-to-string map. Values are stored
// verbatim and are publisher-controlled, so a client MUST escape them when
// rendering — the server only sanitizes error fields, never skill data.
export const skillMetadataSchema = z.record(z.string(), z.string());
export type TSkillMetadata = z.infer<typeof skillMetadataSchema>;

// NOTE: Interpolation values for an issue's i18n message, so a limit reaches
// the translation as `{max}` instead of being spelled out per locale.
export const skillIssueArgsSchema = z
  .record(z.string(), z.union([z.string(), z.number()]))
  .nullable();
export type TSkillIssueArgs = z.infer<typeof skillIssueArgsSchema>;

// NOTE: One manifest failure. `field` names the offending frontmatter or
// metadata key and is null whenever the server could not reflect it safely —
// keep it as data, never interpolate it into markup unescaped.
export const skillManifestIssueSchema = z.object({
  code: z.nativeEnum(ESkillManifestErrorCode),
  messageKey: z.string(),
  field: z.string().nullable(),
  args: skillIssueArgsSchema,
});
export type TSkillManifestIssue = z.infer<typeof skillManifestIssueSchema>;

// NOTE: One bundle problem. Same shape as a manifest issue but keyed by the
// bundle-relative `path` instead of a frontmatter `field`, and its code may be
// either an error or a warning.
export const skillBundleIssueSchema = z.object({
  code: z.union([
    z.nativeEnum(ESkillBundleErrorCode),
    z.nativeEnum(ESkillBundleWarningCode),
  ]),
  messageKey: z.string(),
  path: z.string().nullable(),
  args: skillIssueArgsSchema,
});
export type TSkillBundleIssue = z.infer<typeof skillBundleIssueSchema>;

// NOTE: What AllExceptionsFilter emits, narrowed to the members a skills client
// reads. `issues` is attached by SkillManifestInvalidException and
// `bundleIssues` additionally by SkillBundleInvalidException. `tokenBudget` is
// deliberately absent: it belongs to the chat budget path, not to skills.
export const skillApiErrorSchema = z.object({
  statusCode: z.number(),
  message: z.union([z.string(), z.array(z.string())]),
  errorCode: z.string().optional(),
  retryAfterSeconds: z.number().optional(),
  issues: z.array(skillManifestIssueSchema).optional(),
  bundleIssues: z.array(skillBundleIssueSchema).optional(),
});
export type TSkillApiError = z.infer<typeof skillApiErrorSchema>;

/* -------------------------------------------------------------------------
 * Responses
 * ---------------------------------------------------------------------- */

// NOTE: `body` is null on list rows — a page does not decrypt every skill's
// instructions. `createdAt` is an ISO 8601 string.
export const skillVersionSchema = z.object({
  uuid: z.string(),
  versionNumber: z.number(),
  description: z.string(),
  body: z.string().nullable(),
  license: z.string().nullable(),
  compatibility: z.string().nullable(),
  allowedTools: z.string().nullable(),
  // NOTE: Help text only. It is never interpreted, never executed and never
  // reaches a model prompt — a client renders it beside the skill and nothing
  // else.
  argumentHint: z.string().nullable(),
  // NOTE: `disable-model-invocation` in the manifest. True means the skill is
  // kept out of the catalog the model discovers by itself; a person can still
  // pick it in the chat picker.
  isModelInvocationDisabled: z.boolean(),
  metadata: skillMetadataSchema,
  sourceType: z.nativeEnum(ESkillSourceType),
  sourceUrl: z.string().nullable(),
  sourceRevision: z.string().nullable(),
  contentDigest: z.string(),
  createdAt: z.string(),
});
export type TSkillVersion = z.infer<typeof skillVersionSchema>;

export enum ESkillShareRole {
  VIEWER = "viewer",
  EDITOR = "editor",
}

// NOTE: The caller's effective access to one skill, decided by the server from
// the stored row and re-derived on every request. It exists so web and mobile
// stop guessing whether the caller is the owner, an Editor, a Viewer or merely a
// member of the project that owns a public skill — three independent guesses
// would drift from each other and from the server.
export enum ESkillAccessRole {
  OWNER = "owner",
  EDITOR = "editor",
  VIEWER = "viewer",
  // NOTE: Visible because the skill is public inside the project, not because of
  // any share.
  PROJECT = "project",
}

// NOTE: There is deliberately no DRAFT / SUBMITTED / REJECTED state: an
// authorized curator publishes directly and there is no review queue. DEPRECATED
// still installs, with a warning; REMOVED blocks new installs and drops out of
// normal discovery without touching the project copies already installed.
export enum EOrganizationSkillStatus {
  PUBLISHED = "published",
  DEPRECATED = "deprecated",
  REMOVED = "removed",
}

// NOTE: NSAI-3311 — the catalog link on a skill INSTALLED from the Organization
// Catalog. Its presence is what makes the copy "managed": instructions and
// resources stay read-only while it is linked, and `isUpdateAvailable` is decided
// by the server against the entry's newest installable version rather than by a
// client comparing numbers.
export const installedCatalogLinkSchema = z.object({
  entryUuid: z.string(),
  entryName: z.string(),
  entryStatus: z.nativeEnum(EOrganizationSkillStatus),
  installedVersionUuid: z.string(),
  installedVersionNumber: z.number(),
  installedVersionStatus: z.nativeEnum(EOrganizationSkillStatus),
  latestVersionUuid: z.string().nullable(),
  latestVersionNumber: z.number().nullable(),
  isUpdateAvailable: z.boolean(),
});
export type TInstalledCatalogLink = z.infer<typeof installedCatalogLinkSchema>;

// NOTE: NSAI-5843 — the INVERSE of the link above. That one says "this skill came
// from the catalog"; this one says "a snapshot of this skill is circulating in the
// catalog", which nothing on the skill's own row or detail page used to report.
// The publish dialog warns that a publication "becomes installable by everyone in
// your organization, immediately. There is no second reviewer" — and then that
// fact was visible only from the catalogue page.
export const catalogPublicationLinkSchema = z.object({
  entryUuid: z.string(),
  entryName: z.string(),
  entryStatus: z.nativeEnum(EOrganizationSkillStatus),
  publishedVersionUuid: z.string(),
  publishedVersionNumber: z.number(),
  publishedVersionStatus: z.nativeEnum(EOrganizationSkillStatus),
  // NOTE: Whether the circulating snapshot came from this skill's CURRENT version.
  // False is the surprising case, and the one worth saying out loud: what
  // colleagues install is not what the author is looking at.
  isCurrentVersionPublished: z.boolean(),
});
export type TCatalogPublicationLink = z.infer<
  typeof catalogPublicationLinkSchema
>;

// NOTE: The two roles a share can grant. Editor adds content editing on top of
// Viewer and nothing else — sharing, visibility, ownership, enable/disable,
// archive, delete and catalog publication stay with the owner. A client may show
// the badge from this value but must never infer an owner action from it.
// NOTE: `archivedAt`, `createdAt` and `updatedAt` are ISO 8601 strings.
// NOTE: NSAI-3309 — a skill owner's display identity, resolved by the server through
// the project membership. The SAME shape and the same disclosure the share dialog
// already makes for a fellow member: name and address, nothing else.
export const skillOwnerSchema = z.object({
  projectMemberUuid: z.string(),
  userUuid: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  email: z.string(),
});
export type TSkillOwner = z.infer<typeof skillOwnerSchema>;

export const skillSummarySchema = z.object({
  uuid: z.string(),
  name: z.string(),
  description: z.string(),
  isPublic: z.boolean(),
  isEnabled: z.boolean(),
  isArchived: z.boolean(),
  archivedAt: z.string().nullable(),
  isOwnedByUser: z.boolean(),
  // NOTE: Who owns the skill, so a list can name a colleague instead of saying
  // "project member". Null when the owner no longer resolves as a member of this
  // project (removed or deleted) — render the generic label then. `isOwnedByUser`
  // still decides whether to say "You".
  owner: skillOwnerSchema.nullable(),
  // NOTE: NSAI-3306 — whether the CURRENT version carries executable content.
  // The server answers it for a whole page in one grouped query, so a list can
  // warn without reading each skill's resources. Scripts cannot run in NodeShift
  // yet: this is provenance, never a capability.
  hasScripts: z.boolean(),
  currentVersion: skillVersionSchema.nullable(),
  // NOTE: NSAI-3309 — the caller's effective access, decided by the server. Both
  // clients read this instead of re-deriving owner/Editor/Viewer from
  // `isOwnedByUser` and `isPublic`, which is how the two would drift apart.
  accessRole: z.nativeEnum(ESkillAccessRole),
  // NOTE: NSAI-3311 — null unless this skill was installed from the Organization
  // Catalog and is still linked to it.
  catalog: installedCatalogLinkSchema.nullable(),
  // NOTE: NSAI-5843 — null unless a snapshot of this skill has been published to
  // the Organization Catalog and is still installable. A REMOVED publication is
  // reported as null: removal stops new installs, so it is no longer a copy
  // anybody can obtain.
  catalogPublication: catalogPublicationLinkSchema.nullable(),
  // NOTE: Whether individual sharing is offered for THIS skill — owner, private, not
  // archived, and not a copy managed by the Organization Catalog. A client hides the
  // share action on `false` instead of discovering the refusal by attempting it; the
  // sharing permission is still enforced by the server on the route.
  isShareable: z.boolean(),
  // NOTE: How many live shares the skill has. `null` means this response does not
  // report it — the caller does not own the skill, or the response is a mutation that
  // did not count. `0` means counted and shared with nobody, which is what lets a
  // list tell "private" from "private, shared with N people". Revoked shares and
  // removed project members are excluded, exactly as the read scope excludes them.
  shareCount: z.number().nullable(),
  // NOTE: NSAI-6578 — whether the CALLER, as a share recipient, hid this skill
  // from their own automatic discovery. Always false for the owner and for a
  // public skill. `null` means the response did not report it (a mutation that
  // did not read the share); optional so a response from a server that predates
  // the field still parses. Read it through `isSkillHiddenFromMyDiscovery`.
  isHiddenFromMyDiscovery: z.boolean().nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type TSkillSummary = z.infer<typeof skillSummarySchema>;

// NOTE: The single-skill read answers exactly the list-row shape; the only
// difference is that `currentVersion.body` is populated rather than null. The
// alias exists so a call site can say which of the two it means.
export const skillDetailSchema = skillSummarySchema;
export type TSkillDetail = TSkillSummary;

// NOTE: `createdAt` is an ISO 8601 string. `contentHash` is null when the S3
// backend reported no checksum for the object.
export const skillResourceSchema = z.object({
  uuid: z.string(),
  skillRelativePath: z.string(),
  fileName: z.string(),
  contentType: z.string(),
  fileSizeBytes: z.number(),
  contentHash: z.string().nullable(),
  createdAt: z.string(),
});
export type TSkillResource = z.infer<typeof skillResourceSchema>;

export const skillWithResourcesSchema = skillSummarySchema.extend({
  resources: z.array(skillResourceSchema),
});
export type TSkillWithResources = z.infer<typeof skillWithResourcesSchema>;

export const skillsListResponseSchema = z.object({
  data: z.array(skillSummarySchema),
  total: z.number(),
  page: z.number(),
  limit: z.number(),
  totalPages: z.number(),
});
export type TSkillsListResponse = z.infer<typeof skillsListResponseSchema>;

// NOTE: Every mutating route answers the resource plus a translated `message`
// the client shows as a toast.
export const skillMutationResponseSchema = skillSummarySchema.extend({
  message: z.string(),
});
export type TSkillMutationResponse = z.infer<
  typeof skillMutationResponseSchema
>;

export const skillWithResourcesMutationResponseSchema =
  skillWithResourcesSchema.extend({ message: z.string() });
export type TSkillWithResourcesMutationResponse = z.infer<
  typeof skillWithResourcesMutationResponseSchema
>;

export const deleteSkillResponseSchema = z.object({
  success: z.boolean(),
  message: z.string(),
});
export type TDeleteSkillResponse = z.infer<typeof deleteSkillResponseSchema>;

/* -------------------------------------------------------------------------
 * CRUD requests
 * ---------------------------------------------------------------------- */

const skillNameSchema = z
  .string()
  .min(1)
  .max(MAX_SKILL_NAME_LENGTH)
  .regex(SKILL_NAME_PATTERN);

export const createSkillRequestSchema = z.object({
  name: skillNameSchema,
  description: z.string().min(1).max(MAX_SKILL_DESCRIPTION_LENGTH),
  body: z.string().min(1).max(MAX_SKILL_BODY_LENGTH),
  isPublic: z.boolean(),
  isEnabled: z.boolean().optional(),
  rawFrontmatter: z.string().max(MAX_SKILL_RAW_FRONTMATTER_LENGTH).optional(),
  license: z.string().max(MAX_SKILL_LICENSE_LENGTH).optional(),
  compatibility: z.string().max(MAX_SKILL_COMPATIBILITY_LENGTH).optional(),
  allowedTools: z.string().max(MAX_SKILL_ALLOWED_TOOLS_LENGTH).optional(),
  argumentHint: z.string().max(MAX_SKILL_ARGUMENT_HINT_LENGTH).optional(),
  isModelInvocationDisabled: z.boolean().optional(),
  metadata: skillMetadataSchema.optional(),
});
export type TCreateSkillRequest = z.infer<typeof createSkillRequestSchema>;

// NOTE: Every field optional — omitted means no change. The nullable members
// mirror the server DTO, where null clears a stored value.
export const updateSkillRequestSchema = z.object({
  name: skillNameSchema.optional(),
  description: z.string().min(1).max(MAX_SKILL_DESCRIPTION_LENGTH).optional(),
  body: z.string().min(1).max(MAX_SKILL_BODY_LENGTH).optional(),
  rawFrontmatter: z
    .string()
    .max(MAX_SKILL_RAW_FRONTMATTER_LENGTH)
    .nullable()
    .optional(),
  license: z.string().max(MAX_SKILL_LICENSE_LENGTH).nullable().optional(),
  compatibility: z
    .string()
    .max(MAX_SKILL_COMPATIBILITY_LENGTH)
    .nullable()
    .optional(),
  allowedTools: z
    .string()
    .max(MAX_SKILL_ALLOWED_TOOLS_LENGTH)
    .nullable()
    .optional(),
  argumentHint: z
    .string()
    .max(MAX_SKILL_ARGUMENT_HINT_LENGTH)
    .nullable()
    .optional(),
  isModelInvocationDisabled: z.boolean().optional(),
  metadata: skillMetadataSchema.optional(),
  isPublic: z.boolean().optional(),
  isEnabled: z.boolean().optional(),
  isArchived: z.boolean().optional(),
  // NOTE: NSAI-3309 — explicit consent to destroy every individual share. Turning a
  // shared private skill public, or archiving it, revokes them all and restoring it
  // does not bring them back, so the server refuses such a change without this flag
  // and answers SHARES_CONFIRMATION_REQUIRED. It is not a client courtesy: the
  // server is the gate, and a client that omits the flag gets the 409 rather than a
  // silent revocation.
  confirmRevokeShares: z.boolean().optional(),
});
export type TUpdateSkillRequest = z.infer<typeof updateSkillRequestSchema>;

// NOTE: NSAI-3306 — `search` matches the skill NAME only. Description and body
// are encrypted at rest on the server, so no SQL predicate can reach them
// without decrypting the whole project; a client must not promise otherwise in
// its placeholder text. The needle is trimmed here as well as on the server, so
// a whitespace-only box behaves as "no filter" without a round trip.
export const listSkillsQuerySchema = z.object({
  page: z.number().int().min(MIN_SKILLS_PAGE).optional(),
  limit: z
    .number()
    .int()
    .min(MIN_SKILLS_PAGE)
    .max(MAX_SKILLS_PAGE_SIZE)
    .optional(),
  archived: z.nativeEnum(ESkillArchivedFilter).optional(),
  visibility: z.nativeEnum(ESkillVisibilityFilter).optional(),
  search: z.string().trim().max(MAX_SKILLS_SEARCH_LENGTH).optional(),
  source: z.nativeEnum(ESkillSourceType).optional(),
  enabled: z.nativeEnum(ESkillEnabledFilter).optional(),
});
export type TListSkillsQuery = z.infer<typeof listSkillsQuerySchema>;

/* -------------------------------------------------------------------------
 * Paste import (NSAI-3301)
 * ---------------------------------------------------------------------- */

export const previewPasteImportRequestSchema = z.object({
  skillMd: z.string().min(1),
  isPublic: z.boolean(),
  targetSkillUuid: z.string().uuid().nullable().optional(),
});
export type TPreviewPasteImportRequest = z.infer<
  typeof previewPasteImportRequestSchema
>;

export const importPasteRequestSchema = previewPasteImportRequestSchema.extend({
  requestId: z.string().uuid(),
  isEnabled: z.boolean().optional(),
});
export type TImportPasteRequest = z.infer<typeof importPasteRequestSchema>;

// NOTE: A preview reports parser problems as DATA (`valid: false` with a
// populated `errors` list), not as an HTTP failure. Every content field is null
// when the paste did not parse.
export const skillPastePreviewSchema = z.object({
  valid: z.boolean(),
  name: z.string().nullable(),
  description: z.string().nullable(),
  license: z.string().nullable(),
  compatibility: z.string().nullable(),
  allowedTools: z.string().nullable(),
  argumentHint: z.string().nullable(),
  isModelInvocationDisabled: z.boolean(),
  metadata: skillMetadataSchema,
  bodyTokenCount: z.number(),
  contentDigest: z.string().nullable(),
  warnings: z.array(skillManifestIssueSchema),
  errors: z.array(skillManifestIssueSchema),
});
export type TSkillPastePreview = z.infer<typeof skillPastePreviewSchema>;

/* -------------------------------------------------------------------------
 * Bundle import — ZIP (NSAI-3310) and GitHub (NSAI-4961)
 * ---------------------------------------------------------------------- */

export const presignSkillZipRequestSchema = z.object({
  fileName: z.string().min(1).max(MAX_SKILL_RESOURCE_FILE_NAME_LENGTH),
  contentType: z.string().min(1),
  fileSizeBytes: z.number().int().min(1).max(MAX_SKILL_BUNDLE_ARCHIVE_BYTES),
  checksumSha256: z.string().regex(SKILL_BASE64_SHA256_PATTERN).optional(),
  isPublic: z.boolean(),
  targetSkillUuid: z.string().uuid().nullable().optional(),
});
export type TPresignSkillZipRequest = z.infer<
  typeof presignSkillZipRequestSchema
>;

// NOTE: `uploadUrl` is a short-lived signed PUT. Never log it, never persist
// it, and never render it in an error.
export const presignSkillZipResponseSchema = z.object({
  uploadUrl: z.string(),
  s3Key: z.string(),
  expiresInSeconds: z.number(),
  message: z.string(),
});
export type TPresignSkillZipResponse = z.infer<
  typeof presignSkillZipResponseSchema
>;

export const previewZipImportRequestSchema = z.object({
  s3Key: z.string().min(1).max(MAX_RESOURCE_S3_KEY_LENGTH),
  isPublic: z.boolean(),
  targetSkillUuid: z.string().uuid().nullable().optional(),
});
export type TPreviewZipImportRequest = z.infer<
  typeof previewZipImportRequestSchema
>;

export const importZipRequestSchema = previewZipImportRequestSchema.extend({
  requestId: z.string().uuid(),
  expectedBundleDigest: z.string().regex(SKILL_SHA256_HEX_PATTERN),
  baseVersionUuid: z.string().uuid().nullable().optional(),
  isEnabled: z.boolean().optional(),
});
export type TImportZipRequest = z.infer<typeof importZipRequestSchema>;

// NOTE: The repository, the ref and the folder stay separate fields. That split
// is what makes a repository subpath unrepresentable, so never assemble them
// into one `.../tree/<ref>/<path>` URL before sending.
export const previewGithubImportRequestSchema = z.object({
  repositoryUrl: z.string().min(1).max(MAX_GITHUB_REPOSITORY_URL_LENGTH),
  ref: z.string().min(1).max(MAX_GITHUB_REF_LENGTH),
  path: z.string().max(MAX_GITHUB_PATH_LENGTH),
  githubIntegrationUuid: z.string().uuid().nullable().optional(),
  isPublic: z.boolean(),
  targetSkillUuid: z.string().uuid().nullable().optional(),
});
export type TPreviewGithubImportRequest = z.infer<
  typeof previewGithubImportRequestSchema
>;

export const importGithubRequestSchema =
  previewGithubImportRequestSchema.extend({
    requestId: z.string().uuid(),
    resolvedCommitSha: z.string().regex(SKILL_COMMIT_SHA_HEX_PATTERN),
    expectedBundleDigest: z.string().regex(SKILL_SHA256_HEX_PATTERN),
    baseVersionUuid: z.string().uuid().nullable().optional(),
    isEnabled: z.boolean().optional(),
  });
export type TImportGithubRequest = z.infer<typeof importGithubRequestSchema>;

export const skillBundleResourcePreviewSchema = z.object({
  skillRelativePath: z.string(),
  fileName: z.string(),
  contentType: z.string(),
  fileSizeBytes: z.number(),
  contentHash: z.string(),
  isScript: z.boolean(),
});
export type TSkillBundleResourcePreview = z.infer<
  typeof skillBundleResourcePreviewSchema
>;

// NOTE: A bundle preview carries FOUR issue lists. `errors`/`warnings` describe
// the archive, `manifestErrors`/`manifestWarnings` describe SKILL.md, and a
// caller must read both families — an unparseable manifest leaves `errors`
// empty and explains itself in `manifestErrors`.
// NOTE: NSAI-5805 — what importing a bundle over an existing skill would
// actually do. "Update an existing skill" is a FULL REPLACE with no undo: any
// file the new bundle omits is permanently deleted. The preview said nothing
// about that — it was worded identically to a create preview, and a bundle with
// no resources rendered "This bundle contains no extra files", which reads as
// "nothing extra will be added" rather than as "your existing files are about to
// be deleted".
export const skillBundleTargetDiffSchema = z.object({
  // NOTE: The skill being replaced, named here because the only other place it
  // appeared was the dropdown further up the dialog — out of view by the time
  // someone reads the preview.
  skillName: z.string(),
  addedPaths: z.array(z.string()),
  // NOTE: The load-bearing one: these files are deleted by the import.
  removedPaths: z.array(z.string()),
  changedPaths: z.array(z.string()),
  // NOTE: A count rather than a list — a reader needs to know how much survives
  // untouched, not to read it back.
  unchangedCount: z.number(),
});
export type TSkillBundleTargetDiff = z.infer<
  typeof skillBundleTargetDiffSchema
>;

export const skillBundlePreviewSchema = z.object({
  valid: z.boolean(),
  name: z.string().nullable(),
  description: z.string().nullable(),
  license: z.string().nullable(),
  compatibility: z.string().nullable(),
  allowedTools: z.string().nullable(),
  argumentHint: z.string().nullable(),
  isModelInvocationDisabled: z.boolean(),
  metadata: skillMetadataSchema,
  bodyTokenCount: z.number(),
  rootDirectory: z.string().nullable(),
  resources: z.array(skillBundleResourcePreviewSchema),
  // NOTE: NSAI-5798 — nullable because "how many files does this archive hold"
  // has three answers, not two: a number, zero, and NOBODY KNOWS. A bundle
  // rejected before the reader had counted anything falls in the third, and
  // collapsing that into 0 is what let the panel tell a user their unread
  // archive contained no extra files.
  resourceCount: z.number().nullable(),
  expandedBytes: z.number(),
  archiveBytes: z.number(),
  hasScripts: z.boolean(),
  sourceType: z.nativeEnum(ESkillSourceType),
  sourceUrl: z.string().nullable(),
  sourceRevision: z.string().nullable(),
  sourceRef: z.string().nullable(),
  sourcePath: z.string().nullable(),
  bundleDigest: z.string().nullable(),
  contentDigest: z.string().nullable(),
  targetCurrentVersionUuid: z.string().nullable(),
  // NOTE: Null when the import creates a new skill (nothing can be destroyed) or
  // when the bundle did not parse (no incoming file list to compare against).
  targetDiff: skillBundleTargetDiffSchema.nullable(),
  warnings: z.array(skillBundleIssueSchema),
  errors: z.array(skillBundleIssueSchema),
  manifestWarnings: z.array(skillManifestIssueSchema),
  manifestErrors: z.array(skillManifestIssueSchema),
});
export type TSkillBundlePreview = z.infer<typeof skillBundlePreviewSchema>;

/* -------------------------------------------------------------------------
 * GitHub source updates (NSAI-4961)
 * ---------------------------------------------------------------------- */

// NOTE: The repository, ref and folder are deliberately ABSENT from both bodies:
// they come from the version's stored provenance, so a client cannot point an
// update at a different repository than the skill was imported from.
//
// `githubIntegrationUuid` is a FALLBACK only — the connection the import recorded
// is tried first, and this one is used when that connection is gone or no longer
// usable. There is no token field anywhere in this contract: a private repository
// is reached through a connection the project has already authorized.
export const checkSkillGithubUpdateRequestSchema = z.object({
  githubIntegrationUuid: z.string().uuid().nullable().optional(),
});
export type TCheckSkillGithubUpdateRequest = z.infer<
  typeof checkSkillGithubUpdateRequestSchema
>;

// NOTE: `baseVersionUuid` is REQUIRED here, unlike on an import: an update always
// has a current version, and naming it is what turns a version somebody else
// changed in the meantime into a 409 instead of a silent overwrite.
export const updateSkillGithubSourceRequestSchema =
  checkSkillGithubUpdateRequestSchema.extend({
    requestId: z.string().uuid(),
    resolvedCommitSha: z.string().regex(SKILL_COMMIT_SHA_HEX_PATTERN),
    expectedBundleDigest: z.string().regex(SKILL_SHA256_HEX_PATTERN),
    baseVersionUuid: z.string().uuid(),
  });
export type TUpdateSkillGithubSourceRequest = z.infer<
  typeof updateSkillGithubSourceRequestSchema
>;

// NOTE: Paths only. The change list is what a person reads before confirming, so
// it names files rather than carrying their bytes.
export const skillSourceResourceChangesSchema = z.object({
  added: z.array(z.string()),
  removed: z.array(z.string()),
  modified: z.array(z.string()),
});
export type TSkillSourceResourceChanges = z.infer<
  typeof skillSourceResourceChangesSchema
>;

// NOTE: The manifest half of the diff — the two frontmatter options a person
// cannot see in the resource list. Both sides travel plus the server's own
// verdict on whether they moved, so a client renders "argument hint changed"
// without re-deciding it.
export const skillSourceManifestChangesSchema = z.object({
  isArgumentHintChanged: z.boolean(),
  previousArgumentHint: z.string().nullable(),
  currentArgumentHint: z.string().nullable(),
  isModelInvocationChanged: z.boolean(),
  previousIsModelInvocationDisabled: z.boolean(),
  currentIsModelInvocationDisabled: z.boolean(),
});
export type TSkillSourceManifestChanges = z.infer<
  typeof skillSourceManifestChangesSchema
>;

// NOTE: The update report. `preview` is the ORDINARY bundle preview, unchanged, so
// pipeline problems stay DATA (`valid`, `errors`, `warnings`) and a client renders
// the same panel it already renders for an import. Nothing here is written: a new
// version exists only after the confirm call.
export const skillGithubUpdateCheckSchema = z.object({
  isUpToDate: z.boolean(),
  skillUuid: z.string(),
  repositoryUrl: z.string(),
  ref: z.string(),
  path: z.string(),
  githubIntegrationUuid: z.string().nullable(),
  previousVersionUuid: z.string(),
  previousVersionNumber: z.number(),
  previousCommitSha: z.string().nullable(),
  currentCommitSha: z.string().nullable(),
  previousContentDigest: z.string(),
  currentContentDigest: z.string().nullable(),
  changes: skillSourceResourceChangesSchema,
  manifestChanges: skillSourceManifestChangesSchema,
  preview: skillBundlePreviewSchema,
});
export type TSkillGithubUpdateCheck = z.infer<
  typeof skillGithubUpdateCheckSchema
>;

/* -------------------------------------------------------------------------
 * Resources (NSAI-3303)
 * ---------------------------------------------------------------------- */

export const presignSkillResourceRequestSchema = z.object({
  baseVersionUuid: z.string().uuid(),
  skillRelativePath: z.string().min(1).max(MAX_SKILL_RESOURCE_PATH_BYTES),
  fileName: z.string().min(1).max(MAX_SKILL_RESOURCE_FILE_NAME_LENGTH),
  contentType: z.string().min(1),
  fileSizeBytes: z
    .number()
    .int()
    .min(MIN_SKILL_RESOURCE_BYTES)
    .max(MAX_SKILL_RESOURCE_BYTES),
  checksumSha256: z.string().regex(SKILL_BASE64_SHA256_PATTERN).optional(),
});
export type TPresignSkillResourceRequest = z.infer<
  typeof presignSkillResourceRequestSchema
>;

// NOTE: The caller PUTs the file bytes straight to `uploadUrl`. No file content
// ever travels through the NodeShift API, so there is deliberately no upload
// helper on the API client.
export const presignSkillResourceResponseSchema = z.object({
  uploadUrl: z.string(),
  s3Key: z.string(),
  skillRelativePath: z.string(),
  expiresInSeconds: z.number(),
});
export type TPresignSkillResourceResponse = z.infer<
  typeof presignSkillResourceResponseSchema
>;

// NOTE: Exactly one of `resourceUuid` (carry an existing resource forward) or
// `s3Key` (adopt a freshly uploaded object) is set per entry.
export const skillResourceInputSchema = z.object({
  skillRelativePath: z.string().min(1).max(MAX_SKILL_RESOURCE_PATH_BYTES),
  resourceUuid: z.string().uuid().optional(),
  s3Key: z.string().min(1).max(MAX_RESOURCE_S3_KEY_LENGTH).optional(),
  fileName: z
    .string()
    .min(1)
    .max(MAX_SKILL_RESOURCE_FILE_NAME_LENGTH)
    .optional(),
  contentType: z.string().min(1).optional(),
  fileSizeBytes: z
    .number()
    .int()
    .min(MIN_SKILL_RESOURCE_BYTES)
    .max(MAX_SKILL_RESOURCE_BYTES)
    .optional(),
  checksumSha256: z.string().regex(SKILL_BASE64_SHA256_PATTERN).optional(),
});
export type TSkillResourceInput = z.infer<typeof skillResourceInputSchema>;

// NOTE: PUT, not PATCH — the body is the COMPLETE snapshot for the new version.
// A path the caller omits is removed rather than inherited.
export const updateSkillResourcesRequestSchema = z.object({
  baseVersionUuid: z.string().uuid(),
  resources: z
    .array(skillResourceInputSchema)
    .max(MAX_SKILL_VERSION_RESOURCE_COUNT),
});
export type TUpdateSkillResourcesRequest = z.infer<
  typeof updateSkillResourcesRequestSchema
>;

export const releaseSkillUploadsRequestSchema = z.object({
  s3Keys: z
    .array(z.string().min(1).max(MAX_RESOURCE_S3_KEY_LENGTH))
    .min(1)
    .max(MAX_RELEASE_SKILL_S3_KEYS_PER_REQUEST),
});
export type TReleaseSkillUploadsRequest = z.infer<
  typeof releaseSkillUploadsRequestSchema
>;

export const releaseSkillUploadsResponseSchema = z.object({
  releasedCount: z.number(),
  message: z.string(),
});
export type TReleaseSkillUploadsResponse = z.infer<
  typeof releaseSkillUploadsResponseSchema
>;

// NOTE: The authorized download route answers a short-lived signed GET. The
// server decides visibility; a client never derives a download URL itself.
export const skillResourceDownloadSchema = z.object({
  downloadUrl: z.string(),
  expiresInSeconds: z.number(),
  fileName: z.string(),
  mimeType: z.string(),
  skillRelativePath: z.string(),
});
export type TSkillResourceDownload = z.infer<
  typeof skillResourceDownloadSchema
>;

/* -------------------------------------------------------------------------
 * Runtime (NSAI-3304)
 * ---------------------------------------------------------------------- */

// NOTE: One catalog line — progressive-disclosure level 1. Identity and
// description only, never the instructions body. `isPublic` is present because
// a private and a public skill in one project may share a name.
export const skillCatalogEntrySchema = z.object({
  skillUuid: z.string(),
  versionUuid: z.string(),
  name: z.string(),
  description: z.string(),
  isPublic: z.boolean(),
  isExplicitlySelected: z.boolean(),
});
export type TSkillCatalogEntry = z.infer<typeof skillCatalogEntrySchema>;

// NOTE: Resource metadata as the runtime reports it. `isScript` is derived from
// the stored path and MIME type; `isReadableAsText` says whether the resource
// may be read into a prompt at all.
export const skillRuntimeResourceMetadataSchema = z.object({
  byteSize: z.number(),
  isReadableAsText: z.boolean(),
  isScript: z.boolean(),
  mimeType: z.string(),
  path: z.string(),
  resourceUuid: z.string(),
});
export type TSkillRuntimeResourceMetadata = z.infer<
  typeof skillRuntimeResourceMetadataSchema
>;

// NOTE: The two independent facts a client needs before it can talk about
// scripts. They are separate because a bundle can carry scripts on a
// deployment that has no sandbox, and a deployment can gain a sandbox while a
// given skill carries no scripts at all. `isScriptExecutionAvailable` is false
// everywhere today — no sandbox exists yet.
export interface ISkillScriptState {
  hasScripts: boolean;
  isScriptExecutionAvailable: boolean;
}

// NOTE: The `skillIds` field the chat picker adds to the WS send-message
// payload. It pins skills to ONE sent message and is never persisted as a chat
// setting. The gateway does not validate WebSocket payloads, so the server
// re-authorizes every uuid — this bound is a UI guard, not a security control.
export const chatSkillSelectionSchema = z.object({
  skillIds: z.array(z.string().uuid()).max(MAX_EXPLICIT_SKILL_IDS).optional(),
  // NOTE: NSAI-5925 — the mentions the cap refused, carried alongside the pins so
  // the sent message can state what was asked for and not applied. Names rather
  // than uuids because a refused mention was never resolved to a skill, and the
  // shape check is what keeps the field from becoming a place to park draft text.
  refusedSkillNames: z
    .array(z.string().max(MAX_SKILL_NAME_LENGTH).regex(SKILL_NAME_PATTERN))
    .max(MAX_REFUSED_SKILL_NAMES)
    .optional(),
});
export type TChatSkillSelection = z.infer<typeof chatSkillSelectionSchema>;

/* -------------------------------------------------------------------------
 * Chat picker and activation timeline (NSAI-3308)
 * ---------------------------------------------------------------------- */

// NOTE: One picker row. It carries what a person needs to tell two similarly
// named skills apart and nothing a prompt could consume — no instructions, no
// frontmatter, no resource contents, no S3 key. `GET /skills/chat-picker`
// answers from the same authorized runtime catalog the model sees, so a row
// here is a row the runtime would really load; the management list is NOT the
// picker source (it can include disabled and archived skills).
export const skillPickerEntrySchema = z.object({
  skillUuid: z.string(),
  versionUuid: z.string(),
  name: z.string(),
  description: z.string(),
  isPublic: z.boolean(),
  sourceType: z.nativeEnum(ESkillSourceType),
  sourceRevision: z.string().nullable(),
  // NOTE: The picker lists a manual-only skill on purpose — excluding it from
  // model discovery is exactly what leaves explicit selection as its only way
  // in, so hiding it here would make it unreachable.
  isModelInvocationDisabled: z.boolean(),
  argumentHint: z.string().nullable(),
});
export type TSkillPickerEntry = z.infer<typeof skillPickerEntrySchema>;

export const skillPickerListResponseSchema = z.object({
  data: z.array(skillPickerEntrySchema),
  total: z.number(),
  page: z.number(),
  limit: z.number(),
  totalPages: z.number(),
});
export type TSkillPickerListResponse = z.infer<
  typeof skillPickerListResponseSchema
>;

// NOTE: The picker searches name AND description — the server matches both
// inside the authorized catalog it already decrypts per message, so this is not
// the name-only bound `listSkillsQuerySchema` documents.
export const listSkillPickerQuerySchema = z.object({
  page: z.number().int().min(MIN_SKILLS_PAGE).optional(),
  limit: z
    .number()
    .int()
    .min(MIN_SKILLS_PAGE)
    .max(MAX_SKILLS_PAGE_SIZE)
    .optional(),
  search: z.string().trim().max(MAX_SKILLS_SEARCH_LENGTH).optional(),
  // NOTE: NSAI-5840 — resolve these EXACT names (case-insensitively) rather than
  // searching. The composer sends the `/name` tokens a person typed, because a
  // token can only be pinned once the client holds the skill's uuid, and it used
  // to learn uuids solely from menu results — so a mention typed or pasted
  // without opening the menu resolved to nothing and travelled as plain text.
  names: z
    .array(z.string().trim().max(MAX_SKILLS_SEARCH_LENGTH))
    .max(MAX_SKILL_PICKER_NAMES)
    .optional(),
  // NOTE: NSAI-6395 — resolve these exact UUIDS, the mirror of `names` above, for
  // a client that stores a skill SELECTION and has to name it back. An automation
  // pins skills by uuid, and its form shows them as chips the moment it opens —
  // before any catalog page has been asked for, and for skills that may be pages
  // deep once one has. Bounded by the same limit for the same reason: it is the
  // same question ("which of these skills exist") keyed differently.
  uuids: z.array(z.string()).max(MAX_SKILL_PICKER_NAMES).optional(),
});
export type TListSkillPickerQuery = z.infer<typeof listSkillPickerQuerySchema>;

// NOTE: How a skill entered a message. Mirrors the server enum value for value.
export enum ESkillActivationType {
  AUTOMATIC = "automatic",
  EXPLICIT = "explicit",
}

// NOTE: The only activation outcome vocabulary that reaches a client, and
// deliberately coarse: `UNAVAILABLE` collapses deleted, made private, moved to
// another project, disabled, archived, re-versioned mid-message and
// instructions-too-large, so a timeline entry cannot tell a user WHY a uuid
// they hold is no longer usable. The server keeps its detailed result codes for
// metrics and audit.
export enum ESkillActivationResult {
  LOADED = "loaded",
  UNAVAILABLE = "unavailable",
}

// NOTE: A durable, server-owned timeline entry attached to the assistant
// message that used the skill. `name` is the name AT ACTIVATION TIME, so a
// later rename, archive or delete leaves old history readable; it is null when
// the result is `UNAVAILABLE` because there is no authorized name to show. Only
// the server writes these — a client never reports its own activation.
export const skillActivationRecordSchema = z.object({
  uuid: z.string(),
  skillUuid: z.string(),
  versionUuid: z.string().nullable(),
  name: z.string().nullable(),
  activationType: z.nativeEnum(ESkillActivationType),
  result: z.nativeEnum(ESkillActivationResult),
  position: z.number(),
});
export type TSkillActivationRecord = z.infer<
  typeof skillActivationRecordSchema
>;

// NOTE: The live `skillActivations` socket event. It repeats what the history
// response carries for the same assistant message, so the stream and a refresh
// agree; clients merge by `skillUuid` rather than appending.
export const skillActivationsEventSchema = z.object({
  chatUuid: z.string(),
  messageUuid: z.string(),
  activations: z.array(skillActivationRecordSchema),
});
export type TSkillActivationsEvent = z.infer<
  typeof skillActivationsEventSchema
>;

/* -------------------------------------------------------------------------
 * Private skill sharing (NSAI-3309)
 * ---------------------------------------------------------------------- */

// NOTE: Who a share points at. Returned only by the owner-only share management
// and candidate routes; the "Shared with me" list carries the owner's display
// name and no address, because a recipient cannot manage shares.
export const skillShareMemberSchema = z.object({
  projectMemberUuid: z.string(),
  userUuid: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  email: z.string(),
});
export type TSkillShareMember = z.infer<typeof skillShareMemberSchema>;

// NOTE: `createdAt` / `updatedAt` are ISO 8601 strings.
// NOTE: NSAI-3309 — `canReceiveEditorRole` is advisory and mirrors the field on a
// candidate: whether this recipient currently holds private_skill:write, so a role
// control can disable Editor rather than walk the user into a server refusal. It
// is NOT authority — the server re-checks on every write, because a role can
// change between this response and the PATCH that acts on it.
export const skillShareSchema = z.object({
  uuid: z.string(),
  skillUuid: z.string(),
  role: z.nativeEnum(ESkillShareRole),
  member: skillShareMemberSchema,
  canReceiveEditorRole: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type TSkillShare = z.infer<typeof skillShareSchema>;

export const skillSharesListResponseSchema = z.object({
  data: z.array(skillShareSchema),
  total: z.number(),
  page: z.number(),
  limit: z.number(),
  totalPages: z.number(),
});
export type TSkillSharesListResponse = z.infer<
  typeof skillSharesListResponseSchema
>;

// NOTE: An eligible recipient. `canReceiveEditorRole` reports whether the member
// currently holds private_skill:write — the server refuses an Editor share
// without it, so the picker disables the choice rather than letting the request
// fail. It is advisory: the server re-checks on every write, and a permission
// revoked after this response still blocks the share.
export const skillShareCandidateSchema = skillShareMemberSchema.extend({
  canReceiveEditorRole: z.boolean(),
});
export type TSkillShareCandidate = z.infer<typeof skillShareCandidateSchema>;

export const skillShareCandidatesResponseSchema = z.object({
  data: z.array(skillShareCandidateSchema),
  total: z.number(),
  page: z.number(),
  limit: z.number(),
  totalPages: z.number(),
});
export type TSkillShareCandidatesResponse = z.infer<
  typeof skillShareCandidatesResponseSchema
>;

// NOTE: Recipients are named by PROJECT MEMBER uuid, not user uuid. A membership
// is what the share actually depends on, and resolving it inside the request's
// project makes a cross-project recipient unrepresentable rather than merely
// rejected.
export const createSkillSharesRequestSchema = z.object({
  projectMemberUuids: z
    .array(z.string().uuid())
    .min(1)
    .max(MAX_SKILL_SHARE_RECIPIENTS_PER_REQUEST),
  role: z.nativeEnum(ESkillShareRole),
});
export type TCreateSkillSharesRequest = z.infer<
  typeof createSkillSharesRequestSchema
>;

export const updateSkillShareRequestSchema = z.object({
  role: z.nativeEnum(ESkillShareRole),
});
export type TUpdateSkillShareRequest = z.infer<
  typeof updateSkillShareRequestSchema
>;

export const skillSharesMutationResponseSchema = z.object({
  data: z.array(skillShareSchema),
  message: z.string(),
});
export type TSkillSharesMutationResponse = z.infer<
  typeof skillSharesMutationResponseSchema
>;

export const skillShareMutationResponseSchema = skillShareSchema.extend({
  message: z.string(),
});
export type TSkillShareMutationResponse = z.infer<
  typeof skillShareMutationResponseSchema
>;

// NOTE: A revoke returns no share row — the row is gone. It answers with the flag
// and the localized message the server built, mirroring deleteSkillResponseSchema.
export const revokeSkillShareResponseSchema = z.object({
  success: z.boolean(),
  message: z.string(),
});
export type TRevokeSkillShareResponse = z.infer<
  typeof revokeSkillShareResponseSchema
>;

// NOTE: NSAI-6578 — the recipient's two actions on their own share. Leaving
// answers like a revoke (the row is gone); the discovery preference answers the
// value the server stored, so a client can reconcile an optimistic toggle.
export const leaveSkillShareResponseSchema = revokeSkillShareResponseSchema;
export type TLeaveSkillShareResponse = z.infer<
  typeof leaveSkillShareResponseSchema
>;

export const updateOwnSkillShareRequestSchema = z.object({
  isHiddenFromDiscovery: z.boolean(),
});
export type TUpdateOwnSkillShareRequest = z.infer<
  typeof updateOwnSkillShareRequestSchema
>;

export const updateOwnSkillShareResponseSchema = z.object({
  skillUuid: z.string(),
  isHiddenFromDiscovery: z.boolean(),
  message: z.string(),
});
export type TUpdateOwnSkillShareResponse = z.infer<
  typeof updateOwnSkillShareResponseSchema
>;

export const listSkillSharesQuerySchema = z.object({
  page: z.number().int().min(MIN_SKILLS_PAGE).optional(),
  limit: z
    .number()
    .int()
    .min(MIN_SKILLS_PAGE)
    .max(MAX_SKILLS_PAGE_SIZE)
    .optional(),
});
export type TListSkillSharesQuery = z.infer<typeof listSkillSharesQuerySchema>;

// NOTE: Paging plus a search needle, bounded by the same length the skills list
// uses. Declared ONCE because both share-scoped searchable lists take exactly this
// query — the owner's candidate picker and the recipient's own "shared with me"
// list — instead of one of them re-stating the shape inline. The alias below only
// gives the picker the name it reads by.
export const listSkillSharesSearchQuerySchema =
  listSkillSharesQuerySchema.extend({
    search: z.string().trim().max(MAX_SKILLS_SEARCH_LENGTH).optional(),
  });
export type TListSkillSharesSearchQuery = z.infer<
  typeof listSkillSharesSearchQuerySchema
>;

// NOTE: The candidate search matches a member's name and address.
export const listSkillShareCandidatesQuerySchema =
  listSkillSharesSearchQuerySchema;
export type TListSkillShareCandidatesQuery = TListSkillSharesSearchQuery;

// NOTE: Stable codes for the sharing failures a client must tell apart. Mapped to
// an i18n key by buildSkillShareMessageKey — a server message is never rendered.
export enum ESkillShareErrorCode {
  SHARE_TARGET_INVALID = "SHARE_TARGET_INVALID",
  SHARE_DUPLICATE = "SHARE_DUPLICATE",
  SHARE_RECIPIENT_PERMISSION_MISSING = "SHARE_RECIPIENT_PERMISSION_MISSING",
  SHARE_CONCURRENT_UPDATE = "SHARE_CONCURRENT_UPDATE",
  // NOTE: Returned when a visibility change or an archive would revoke live
  // shares and the request did not carry the explicit confirmation.
  SHARES_CONFIRMATION_REQUIRED = "SHARES_CONFIRMATION_REQUIRED",
  SHARE_NOT_ALLOWED = "SHARE_NOT_ALLOWED",
  // NOTE: The skill is a copy installed from the Organization Catalog. Its content
  // is managed by the entry and anyone entitled can install it themselves, so an
  // individual share would only add a second, divergent path to managed content.
  SHARE_CATALOG_MANAGED = "SHARE_CATALOG_MANAGED",
}

/* -------------------------------------------------------------------------
 * Organization Catalog (NSAI-3311)
 * ---------------------------------------------------------------------- */

// NOTE: One immutable published version. Every field is a snapshot taken at
// publication, never a live read of the source skill, so a later edit to that
// skill cannot change what this version says. There is no `body` and no resource
// bytes here: the catalog answers the install decision, not the content.
export const organizationCatalogVersionSchema = z.object({
  uuid: z.string(),
  versionNumber: z.number(),
  name: z.string(),
  description: z.string(),
  contentDigest: z.string(),
  license: z.string().nullable(),
  compatibility: z.string().nullable(),
  sourceType: z.nativeEnum(ESkillSourceType),
  sourceUrl: z.string().nullable(),
  // NOTE: The pinned commit for a repository import.
  sourceRevision: z.string().nullable(),
  sourceRef: z.string().nullable(),
  sourcePath: z.string().nullable(),
  // NOTE: The published version carries executable files. Scripts are data until
  // PR 8 (NSAI-3305 sandbox); this drives a warning and an acknowledgement, never
  // an execution capability.
  hasScripts: z.boolean(),
  status: z.nativeEnum(EOrganizationSkillStatus),
  publisherName: z.string().nullable(),
  // NOTE: ISO 8601 string.
  publishedAt: z.string(),
});
export type TOrganizationCatalogVersion = z.infer<
  typeof organizationCatalogVersionSchema
>;

// NOTE: A catalog row. `installedSkillUuid` / `installedVersionUuid` /
// `isUpdateAvailable` describe the CALLER's own install in the project the
// request carried, so a list can render "Installed" and "Update available"
// without a second query — and so no client computes "newer" by comparing
// version numbers it might not have.
export const organizationCatalogEntrySchema = z.object({
  uuid: z.string(),
  name: z.string(),
  status: z.nativeEnum(EOrganizationSkillStatus),
  latestVersion: organizationCatalogVersionSchema.nullable(),
  versionCount: z.number(),
  installedSkillUuid: z.string().nullable(),
  installedVersionUuid: z.string().nullable(),
  isUpdateAvailable: z.boolean(),
  // NOTE: Enough state to render the right action WITHOUT attempting an install
  // first: `isInstalled` means the caller already has this entry here,
  // `isSourceInProject` means this project published it (so there is nothing to
  // install), and `isNameTakenInProject` means the caller owns another private skill
  // of this name. An Install action offered while any of the last two holds is
  // guaranteed to 409.
  isInstalled: z.boolean(),
  isSourceInProject: z.boolean(),
  isNameTakenInProject: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type TOrganizationCatalogEntry = z.infer<
  typeof organizationCatalogEntrySchema
>;

// NOTE: The detail read adds the version history, so a curator can retire one
// exact version and a member can see what a pinned install predates.
export const organizationCatalogEntryDetailSchema =
  organizationCatalogEntrySchema.extend({
    versions: z.array(organizationCatalogVersionSchema),
  });
export type TOrganizationCatalogEntryDetail = z.infer<
  typeof organizationCatalogEntryDetailSchema
>;

export const organizationCatalogListResponseSchema = z.object({
  data: z.array(organizationCatalogEntrySchema),
  total: z.number(),
  page: z.number(),
  limit: z.number(),
  totalPages: z.number(),
});
export type TOrganizationCatalogListResponse = z.infer<
  typeof organizationCatalogListResponseSchema
>;

export const listOrganizationCatalogQuerySchema = z.object({
  page: z.number().int().min(MIN_SKILLS_PAGE).optional(),
  limit: z
    .number()
    .int()
    .min(MIN_SKILLS_PAGE)
    .max(MAX_SKILLS_PAGE_SIZE)
    .optional(),
  search: z.string().trim().max(MAX_SKILLS_SEARCH_LENGTH).optional(),
  status: z.nativeEnum(EOrganizationSkillStatus).optional(),
});
export type TListOrganizationCatalogQuery = z.infer<
  typeof listOrganizationCatalogQuerySchema
>;

// NOTE: Publishing pins ONE exact existing version — both uuids are required, so
// "publish the latest" is not expressible and a concurrent edit to the source
// skill cannot silently change what gets published.
export const publishOrganizationSkillRequestSchema = z.object({
  requestId: z.string().uuid(),
  skillUuid: z.string().uuid(),
  skillVersionUuid: z.string().uuid(),
  acknowledgeScripts: z.boolean().optional(),
});
export type TPublishOrganizationSkillRequest = z.infer<
  typeof publishOrganizationSkillRequestSchema
>;

// NOTE: Direct catalog import. Each shape is the existing import request minus
// `isPublic` and `targetSkillUuid`: a catalog publication always creates a fresh
// PRIVATE source skill in the curator's project and publishes that exact version,
// as one idempotent server operation keyed by `requestId`.
export const publishOrganizationSkillPasteRequestSchema = z.object({
  requestId: z.string().uuid(),
  skillMd: z.string().min(1),
  acknowledgeScripts: z.boolean().optional(),
});
export type TPublishOrganizationSkillPasteRequest = z.infer<
  typeof publishOrganizationSkillPasteRequestSchema
>;

export const publishOrganizationSkillZipRequestSchema = z.object({
  requestId: z.string().uuid(),
  s3Key: z.string().min(1).max(MAX_RESOURCE_S3_KEY_LENGTH),
  expectedBundleDigest: z.string().regex(SKILL_SHA256_HEX_PATTERN),
  acknowledgeScripts: z.boolean().optional(),
});
export type TPublishOrganizationSkillZipRequest = z.infer<
  typeof publishOrganizationSkillZipRequestSchema
>;

export const publishOrganizationSkillGithubRequestSchema = z.object({
  requestId: z.string().uuid(),
  repositoryUrl: z.string().min(1).max(MAX_GITHUB_REPOSITORY_URL_LENGTH),
  ref: z.string().min(1).max(MAX_GITHUB_REF_LENGTH),
  path: z.string().min(1).max(MAX_GITHUB_PATH_LENGTH),
  githubIntegrationUuid: z.string().uuid().nullable().optional(),
  resolvedCommitSha: z.string().regex(SKILL_COMMIT_SHA_HEX_PATTERN),
  expectedBundleDigest: z.string().regex(SKILL_SHA256_HEX_PATTERN),
  acknowledgeScripts: z.boolean().optional(),
});
export type TPublishOrganizationSkillGithubRequest = z.infer<
  typeof publishOrganizationSkillGithubRequestSchema
>;

// NOTE: Only the two retiring transitions are expressible. A version cannot be
// un-deprecated or un-removed through this route: publishing a newer version is
// the forward path, which keeps the published history append-only.
export const updateOrganizationCatalogVersionStatusRequestSchema = z.object({
  status: z.enum([
    EOrganizationSkillStatus.DEPRECATED,
    EOrganizationSkillStatus.REMOVED,
  ]),
});
export type TUpdateOrganizationCatalogVersionStatusRequest = z.infer<
  typeof updateOrganizationCatalogVersionStatusRequestSchema
>;

// NOTE: Installing names the exact catalog version, so "install" and "install the
// latest" cannot diverge. `acknowledgeScripts` is required by the server whenever
// the version carries executable files, mirroring the ZIP/GitHub import gate.
export const installOrganizationCatalogSkillRequestSchema = z.object({
  requestId: z.string().uuid(),
  catalogVersionUuid: z.string().uuid(),
  acknowledgeScripts: z.boolean().optional(),
});
export type TInstallOrganizationCatalogSkillRequest = z.infer<
  typeof installOrganizationCatalogSkillRequestSchema
>;

// NOTE: An update is never implicit: it names both the installed skill and the
// exact catalog version replacing its content. There is no server path that
// re-points an installed skill without this request.
export const updateInstalledCatalogSkillRequestSchema =
  installOrganizationCatalogSkillRequestSchema.extend({
    targetSkillUuid: z.string().uuid(),
  });
export type TUpdateInstalledCatalogSkillRequest = z.infer<
  typeof updateInstalledCatalogSkillRequestSchema
>;

export const organizationCatalogEntryMutationResponseSchema =
  organizationCatalogEntryDetailSchema.extend({
    message: z.string(),
  });
export type TOrganizationCatalogEntryMutationResponse = z.infer<
  typeof organizationCatalogEntryMutationResponseSchema
>;

// NOTE: Stable codes for the catalog failures a client must tell apart. Mapped to
// an i18n key by buildOrganizationSkillMessageKey.
export enum EOrganizationSkillErrorCode {
  CATALOG_NOT_FOUND = "CATALOG_NOT_FOUND",
  CATALOG_VERSION_ALREADY_PUBLISHED = "CATALOG_VERSION_ALREADY_PUBLISHED",
  CATALOG_VERSION_DEPRECATED = "CATALOG_VERSION_DEPRECATED",
  CATALOG_VERSION_REMOVED = "CATALOG_VERSION_REMOVED",
  CATALOG_INSTALL_DUPLICATE = "CATALOG_INSTALL_DUPLICATE",
  // NOTE: The project already holds the skill the entry was published from, so the
  // install would collide on the private owner/name index. Distinct from an
  // unrelated name clash, which still arrives as a generic 409 with no code.
  CATALOG_SOURCE_SKILL_IN_PROJECT = "CATALOG_SOURCE_SKILL_IN_PROJECT",
  CATALOG_UPDATE_CONFLICT = "CATALOG_UPDATE_CONFLICT",
  // NOTE: The pinned source version is gone or no longer matches its recorded
  // digest. Deliberately one code for both: telling them apart would report on
  // another project's data.
  CATALOG_SOURCE_UNAVAILABLE = "CATALOG_SOURCE_UNAVAILABLE",
  CATALOG_RESOURCE_COPY_FAILED = "CATALOG_RESOURCE_COPY_FAILED",
  CATALOG_SCRIPTS_ACKNOWLEDGEMENT_REQUIRED = "CATALOG_SCRIPTS_ACKNOWLEDGEMENT_REQUIRED",
  CATALOG_NOT_ALLOWED_TO_CURATE = "CATALOG_NOT_ALLOWED_TO_CURATE",
  CATALOG_NOT_ALLOWED_TO_INSTALL = "CATALOG_NOT_ALLOWED_TO_INSTALL",
}
